import { useQuery } from '@apollo/client';
import { Card, Col, Row, Typography } from 'antd';
import { Loading } from 'app/components/Loading';
import { GET_INITIATIVE_USHER_STEP_RESULT } from 'app/graphql/queries';
import dayjs from 'dayjs';
import React, { memo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components/macro';

const { Title, Text } = Typography;

export const Results = memo(() => {
  const { resultsID } = useParams();

  const { loading, data } = useQuery(GET_INITIATIVE_USHER_STEP_RESULT, {
    variables: { id: resultsID },
  });

  if (loading) return <Loading />;

  const result = data?.initiativeUsherStepResult;
  const initiativeUsherObj = result?.step?.initiativeUsher;

  return (
    <Div>
      <Title level={2} style={{ marginBottom: '24px' }}>
        View Results
      </Title>

      {/* General Information */}
      <Card title="General Information" className="mb-3">
        <Row gutter={[8, 8]}>
          <Col md={8} xs={24}>
            <Text strong>Usher ID:</Text>&nbsp;
            {initiativeUsherObj?.usher?.usherId || '-'}
          </Col>

          <Col md={8} xs={24}>
            <Text strong>Usher Name:</Text>&nbsp;
            {initiativeUsherObj?.usher?.englishFullName || '-'}
          </Col>

          <Col md={8} xs={24}>
            <Text strong>Initiative:</Text>&nbsp;
            {initiativeUsherObj?.initiative?.englishName || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col md={8} xs={24}>
            <Text strong>Interview Date:</Text>&nbsp;
            {result.interviewDate
              ? dayjs(result.interviewDate).format('DD-MM-YYYY')
              : '-'}
          </Col>

          <Col md={8} xs={24}>
            <Text strong>Interview Time:</Text>&nbsp;
            {result.interviewTime
              ? dayjs(
                  `${dayjs().format('YYYY-MM-DD')} ${result.interviewTime}`,
                  'YYYY-MM-DD HH:mm:ss',
                ).format('hh:mm A')
              : '-'}
          </Col>

          <Col md={8} xs={24}>
            <Text strong>Assessor Name:</Text> {result.assessorName || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col xs={24}>
            <Text strong>Comments:</Text> {result.generalComments || '-'}
          </Col>
        </Row>
      </Card>

      {/* Interviewer Section */}
      <Card title="Interviewer Section" className="mb-3">
        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Text strong>English Level:</Text> {result.englishLevel || '-'}
          </Col>

          <Col md={12} xs={24}>
            <Text strong>Arabic Level:</Text> {result.arabicLevel || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Text strong>Work Experience:</Text> {result.workExperience || '-'}
          </Col>

          <Col md={12} xs={24}>
            <Text strong>Comments:</Text> {result.workExperienceComments || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Text strong>Volunteering Experience:</Text>&nbsp;
            {result.volunteeringExperience || '-'}
          </Col>

          <Col md={12} xs={24}>
            <Text strong>Comments:</Text>&nbsp;
            {result.volunteeringExperienceComments || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col xs={24}>
            <Text strong>Why did you apply for this program?</Text>&nbsp;
            {result.applyReasons || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col xs={24}>
            <Text strong>Comments:</Text>&nbsp;
            {result.interviewerSectionComments || '-'}
          </Col>
        </Row>
      </Card>

      {/* Personal Skills */}
      <Card title="Personal Skills" className="mb-3">
        {[
          {
            label: 'Dealing with Kids',
            rating: 'dealingWithKidsRating',
            comments: 'dealingWithKidsComments',
          },
          {
            label: 'Leadership Skills',
            rating: 'leadershipSkillsRating',
            comments: 'leadershipSkillsComments',
          },
          {
            label: 'Work Under Stressful Situations',
            rating: 'stressfulSituationsRating',
            comments: 'stressfulSituationsComments',
          },
          {
            label: 'Social Intelligence',
            rating: 'socialIntelligenceRating',
            comments: 'socialIntelligenceComments',
          },
          {
            label: 'If your leader is not fair',
            rating: 'unfairLeaderRating',
            comments: 'unfairLeaderComments',
          },
        ].map((item, idx) => (
          <Row gutter={[8, 8]} key={idx}>
            <Col md={12} xs={24}>
              <Text strong>{item.label}:</Text> {result[item.rating] || '-'}
            </Col>

            <Col md={12} xs={24}>
              <Text strong>Comments:</Text> {result[item.comments] || '-'}
            </Col>
          </Row>
        ))}
      </Card>

      {/* Usher Experience Evaluation */}
      <Card title="Usher Experience Evaluation" className="mb-3">
        {[
          {
            label: 'Confidence while speaking',
            rating: 'confidenceRating',
            comments: 'confidenceComments',
          },
          {
            label: 'Enthusiasm while speaking',
            rating: 'enthusiasmRating',
            comments: 'enthusiasmComments',
          },
          {
            label: 'Appropriate and varied volume',
            rating: 'speakingVolumeRating',
            comments: 'speakingVolumeComments',
          },
          {
            label: 'Good use of eye contact',
            rating: 'eyeContactRating',
            comments: 'eyeContactComments',
          },
        ].map((item, idx) => (
          <Row gutter={[8, 8]} key={idx}>
            <Col md={12} xs={24}>
              <Text strong>{item.label}:</Text> {result[item.rating] || '-'}
            </Col>

            <Col md={12} xs={24}>
              <Text strong>Comments:</Text> {result[item.comments] || '-'}
            </Col>
          </Row>
        ))}
      </Card>

      {/* Decision & Final Comments */}
      <Card title="Decision & Final Comments" className="mb-3">
        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Text strong>Decision:</Text> {result.decision || '-'}
          </Col>

          <Col md={12} xs={24}>
            <Text strong>Rate:</Text> {result.rate || '-'}
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col xs={24}>
            <Text strong>Final Comments:</Text> {result.finalComments || '-'}
          </Col>
        </Row>
      </Card>
    </Div>
  );
});

const Div = styled.div`
  padding: 24px;
`;
